import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Link, Image, Text, Heading3, ButtonLink } from '@blueprint/ui';

export const PropertyNewsWrapper = styled.div(
    ({ theme: { spacing, colors, mq } }) =>
        css`
            width: 100%;
            height: auto;
            background-color: ${colors.white};
            margin-top: ${spacing['24px']};
            justify-content: center;
            align-items: center;
            padding: 0 ${spacing['16px']};

            ${mq.medium} {
                padding: 0;
            }
        `
);

export const ArticleLarge = styled('article')(
    ({ theme: { mq, colors, spacing, focusRing } }) =>
        css`
            display: block;
            position: relative;
            width: 100%;
            ${Link} {
                color: ${colors['grey-slate']};
            }

            ${mq.medium} {
                display: flex;
            }

            &:hover ${StyledImage} {
                opacity: 0.7;
                transition: 0.5s ease;
            }

            &:active {
                opacity: 0.7;
                transition: 0.5s ease;
            }

            &:focus {
                box-shadow: ${focusRing};
                border-radius: ${spacing['8px']};
                outline: 0;
                z-index: 1;
            }
        `
);

export const ImageContainer = styled.div(
    ({ theme: { spacing, borderRadius } }) =>
        css`
            height: auto;
            display: flex;
            flex-direction: row;
            border-radius: ${borderRadius['8px']};
            padding-bottom: ${spacing['16px']};
            img {
                border-radius: ${borderRadius['8px']};
                object-fit: cover;
            }
        `
);

export const MainImageContainer = styled.div(
    ({ theme: { mq } }) =>
        css`
            width: 100%;

            img {
                object-fit: cover;
            }

            ${mq.medium} {
                width: 50%;
            }
        `
);

export const ArticleSmall = styled('article')(
    ({ theme: { spacing, colors, mq } }) =>
        css`
            display: block;
            width: 100%;
            padding: ${spacing['16px']} 0 0 0;
            position: relative;
            ${Link} {
                color: ${colors['grey-slate']};
            }
            ${mq.medium} {
                padding: ${spacing['32px']} 0 0 0;
                &:nth-child(2) {
                    padding-left: ${spacing['24px']};
                    padding-right: ${spacing['24px']};
                }
            }

            &:hover ${ImageContainer} {
                opacity: 0.7;
                transition: 0.5s ease;
            }

            &:active {
                opacity: 0.7;
                transition: 0.5s ease;
            }
        `
);

export const ArticleWrapper = styled.div(
    ({ theme: { mq } }) =>
        css`
            display: block;
            width: 100%;

            ${mq.medium} {
                display: flex;
            }
        `
);

export const StyledHeading = styled(Heading3)(
    ({ theme: { spacing } }) => css`
        padding: ${spacing['8px']} 0 ${spacing['16px']} 0;
        width: 100%;
    `
);

export const StyledImage = styled(Image)(
    ({ theme: { borderRadius, mq } }) =>
        css`
            display: block;
            border-radius: ${borderRadius['8px']};
            width: 100%;
            min-height: 214px;

            ${mq.medium} {
                width: 100%;
                min-height: 218px;
            }

            ${mq.large} {
                min-height: 333px;
            }
        `
);

export const StyledSmallArticleImage = styled(Image)(
    ({ theme: { mq } }) =>
        css`
            width: 100%;
            border-radius: 8px;
            object-fit: cover;
            height: 214px;

            ${mq.medium} {
                height: 133px;
            }

            ${mq.large} {
                height: 216px;
            }
        `
);

export const MainImageHeading = styled(Heading3)(
    ({ theme: { spacing, fontSizes } }) => css`
        padding: ${spacing['8px']} 0 ${spacing['16px']} 0;
        font-size: ${fontSizes['9']};
        font-weight: bold;
        line-height: ${fontSizes['7']};
    `
);

export const MainArticleInfoWrapper = styled.div(
    ({ theme: { spacing, mq } }) => css`
        padding: ${spacing['16px']} 0 0 0;
        width: 100%;

        ${mq.medium} {
            width: 45%;
            padding: 0 ${spacing['16px']} 0 ${spacing['16px']};
        }

        ${mq.large} {
            padding: ${spacing['16px']};
        }
    `
);

export const MainArticleWrapperWithFocus = styled.div(
    ({ theme: { focusRing, spacing, mq } }) => css`
        &:focus {
            box-shadow: ${focusRing};
            outline: 0;
            z-index: 1;
            border-radius: ${spacing['16px']};
        }

        ${mq.medium} {
            display: flex;
        }
    `
);

/* This ::before link extends the focusable area around
 *   the components container all the way up to its parent.
 * Done this way for accessibility to keep only the relevant
 *   content as a link, rather than wrapping the whole component.
 * Note: The parent/wrapper needs `position: relative` for
 * this to work!
 */
export const LinkFocus = styled(Link)(
    () => css`
        &::before {
            bottom: 0;
            content: '';
            cursor: pointer;
            left: 0;
            overflow: hidden;
            position: absolute;
            right: 0;
            top: 0;
            z-index: 1;
        }

        &:focus {
            display: block;
        }
    `
);

export const MainArticleSnippet = styled(Text)(
    ({ theme: { mq } }) => css`
        display: none;

        ${mq.medium} {
            display: block;
        }
    `
);

export const StyledButton = styled(ButtonLink)(
    ({ theme: { mq } }) => css`
        display: none;

        ${mq.medium} {
            display: block;
        }
    `
);

export const StyledButtonMobile = styled(ButtonLink)(
    ({ theme: { mq, spacing } }) => css`
        display: block;
        padding: ${spacing['32px']} 0 0 0;

        ${mq.medium} {
            display: none;
        }
    `
);

export const MainText = styled(Text)(
    ({ theme: { mq, spacing } }) => css`
        padding: ${spacing['8px']} 0 ${spacing['32px']} 0;

        ${mq.medium} {
            display: block;
        }
    `
);

export const HeadingWrapper = styled.div(
    ({ theme: { mq } }) => css`
        display: block;

        ${mq.medium} {
            display: flex;
            justify-content: space-between;
        }
    `
);
